import qs from 'qs'
import jsonp, { JsonpConfig } from 'jsonp'
import { AxiosInstance, AxiosPromise, AxiosRequestConfig } from 'axios'
import { App } from 'vue'
import axiosInstance from './axios'
import { Http, Params } from '@/plugins/http/http.interface'
import { ssePost } from './sse'

const convertDataToQuery = (data: Params) => {
  let param = ''
  if (Object.prototype.toString.call(data) === '[object Object]') {
    param = ''
    Object.keys(data).forEach(key => {
      param += `${key}=${data[key] ? data[key] : ''}&`
    })
    if (param.length > 0) param = param.substring(0, param.length - 1)
  } else {
    throw new Error('参数错误，data接收为一个对象')
  }
  return param
}

const http = (axios: AxiosInstance): Http => {
  return {
    /**
     * 普通get请求
     * @param url 请求地址
     * @param data Object 地址参数
     * @param config 请求config
     * @returns {*} Promise 请求结果
     */
    get (url, data, config) {
      const query = convertDataToQuery(Object.assign(data || {}, { _t: new Date().getTime() }))
      url += `${url.indexOf('?') > -1 ? '&' : '?'}` + query
      return axios.get(url, config)
    },
    /**
     * 普通post请求
     * @param url 请求地址
     * @param data Object 请求body
     * @param config 请求config 默认contentType:application/json;charset=UTF-8
     * @returns {*} Promise 请求结果
     */
    post (url: string, data: Params, config: AxiosRequestConfig) {
      return axios.post(url, data, config)
    },
    /**
     * post表单请求
     * @param url 请求地址
     * @param data Object 请求参数 Object
     * @param config 请求config
     * @returns {*} Promise 请求结果
     */
    postForm (url: string, data: Params, config: AxiosRequestConfig) {
      if (config) {
        config.headers = config.headers || {}
        config.headers['content-type'] =
          'application/x-www-form-urlencoded; charset=UTF-8'
      } else {
        config = {
          headers: {
            'content-type': 'application/x-www-form-urlencoded; charset=UTF-8'
          }
        }
      }
      return axios.post(url, data ? qs.stringify(data) : null, config)
    },
    /**
     * post请求上传文件
     * @param url 请求地址
     * @param data Object 请求参数 Object
     * @param config 请求config
     * @returns {*} Promise 请求结果
     */
    postFile (url: string, data: FormData, config: AxiosRequestConfig = {}) {
      // 不要手写 multipart Content-Type：缺少 boundary 时服务端无法解析文件字段
      return axios.post(url, data, config)
    },
    /**
     * post请求下载文件
     * @param url 请求地址
     * @param data Object 请求参数 Object
     * @param config 请求config
     * @returns {*} Promise 请求结果
     */
    download (url: string, data: Params = {}, config: AxiosRequestConfig = {}): AxiosPromise<BlobPart> {
      config = {
        responseType: 'blob'
      }
      return axios.post(url, data, config)
    },
    /**
     * patch请求
     * @param url 请求地址
     * @param data Object 请求body
     * @param config 请求config 默认contentType:application/json;charset=UTF-8
     * @returns {*} Promise 请求结果
     */
    patch (url: string, data: Params, config: AxiosRequestConfig) {
      return axios.patch(url, data, config)
    },
    /**
     * delete请求
     * @param url 请求地址
     * @param data Object 地址参数
     * @param config 请求config
     * @returns {*} Promise 请求结果
     */
    delete (url: string, data: Params, config: AxiosRequestConfig) {
      const query = convertDataToQuery(Object.assign(data || {}, { _t: new Date().getTime() }))
      url += `${url.indexOf('?') > -1 ? '&' : '?'}` + query
      return axios.delete(url, config)
    },
    /**
     * jsonp请求
     * @param url 请求地址
     * @param data Object 地址参数
     * @param config jsonp配置 {param:'查询字符串参数的名称',timeout:'超时时间', prefix:'前缀', name:''}
     * @returns {Promise} 请求结果
     */
    jsonp (url: string, data: Params, config: JsonpConfig) {
      url += (url.indexOf('?') > -1) ? '&' : '?' + convertDataToQuery(data)
      return new Promise((resolve, reject) => {
        jsonp(url, config, (error: Error, data: unknown) => {
          if (!error) {
            resolve(data)
          } else {
            reject(error)
          }
        })
      })
    },
    ssePost
  }
}

export const useHttp = (): Http => {
  return http(axiosInstance)
}

export default function (app: App): void {
  app.config.globalProperties.$http = http(axiosInstance)
}
